import { useRef, useEffect } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { useMouseLerp } from './useMouseLerp';

/**
 * Camera path keyframes — one per "station."
 * Each entry: { pos: [x,y,z], rot: [rx,ry,rz] }
 */
const KEYFRAMES = [
  { pos: [0,  0,  14], rot: [0,     0, 0] }, // Station 1 — Hero
  { pos: [0,  0,   8], rot: [0,     0, 0] }, // Station 2 — Phone
  { pos: [2,  0,   4], rot: [0,  -0.1, 0] }, // Station 3 — Data
  { pos: [3,  1,   1], rot: [0,  -0.2, 0] }, // Station 4 — Wearables
  { pos: [0, -1,  -5], rot: [0.12,  0, 0] }, // Station 5 — Infrastructure
  { pos: [0,  0, -13], rot: [0,     0, 0] }, // Station 6 — Business
];

function lerp(a, b, t) { return a + (b - a) * t; }

function getKeyframeAtProgress(progress) {
  const total = KEYFRAMES.length - 1;
  const scaled = progress * total;
  const idx = Math.min(Math.floor(scaled), total - 1);
  const t = scaled - idx;
  const from = KEYFRAMES[idx];
  const to = KEYFRAMES[idx + 1];
  return {
    pos: from.pos.map((v, i) => lerp(v, to.pos[i], smoothstep(t))),
    rot: from.rot.map((v, i) => lerp(v, to.rot[i], smoothstep(t))),
  };
}

function smoothstep(t) {
  return t * t * (3 - 2 * t);
}

export default function CameraManager({ scrollProgress }) {
  const { camera } = useThree();
  const targetPos = useRef({ pos: [0, 0, 14], rot: [0, 0, 0] });
  const currentPos = useRef({ pos: [0, 0, 14], rot: [0, 0, 0] });
  const { mouse } = useMouseLerp();

  useEffect(() => {
    const kf = getKeyframeAtProgress(scrollProgress);
    targetPos.current = kf;
  }, [scrollProgress]);

  useFrame(() => {
    const lerpFactor = 0.06;
    const tp = targetPos.current;
    const cp = currentPos.current;

    cp.pos = cp.pos.map((v, i) => lerp(v, tp.pos[i], lerpFactor));
    cp.rot = cp.rot.map((v, i) => lerp(v, tp.rot[i], lerpFactor));

    camera.position.set(...cp.pos);

    // Apply scroll rotation + subtle mouse tilt
    camera.rotation.x = cp.rot[0] + mouse.y * 0.04;
    camera.rotation.y = cp.rot[1] + mouse.x * 0.06;
    camera.rotation.z = cp.rot[2];
  });

  return null;
}
